import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type DataRetentionPolicy,
  type DeletionRequestAdmin,
  type DeletionRequestResult,
  type DataExportResult,
  type ProcessDeletionsResult,
} from '../api/client';

// ─── Public: Deletion / Export Requests ─────────────────────────────────────

export function useRequestDeletion() {
  return useMutation<DeletionRequestResult, Error, string>({
    mutationFn: (email: string) => api.privacy.requestDeletion(email),
  });
}

export function useRequestExport() {
  return useMutation<DataExportResult, Error, string>({
    mutationFn: (email: string) => api.privacy.requestExport(email),
  });
}

// ─── Host: Data Retention ───────────────────────────────────────────────────

export function useRetentionPolicy() {
  return useQuery<DataRetentionPolicy>({
    queryKey: ['account', 'retention-policy'],
    queryFn: () => api.account.getRetentionPolicy(),
  });
}

export function useUpdateRetentionPolicy() {
  const queryClient = useQueryClient();
  return useMutation<
    DataRetentionPolicy,
    Error,
    { retainGuestDataDays: number; autoDeleteEnabled: boolean }
  >({
    mutationFn: (data) => api.account.updateRetentionPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'retention-policy'] });
    },
  });
}

// ─── Admin: Privacy Requests ────────────────────────────────────────────────

export function useAdminPrivacyRequests(page = 1, limit = 20) {
  return useQuery<{
    requests: DeletionRequestAdmin[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>({
    queryKey: ['admin', 'privacy', 'requests', page, limit],
    queryFn: () => api.admin.privacy.listRequests(page, limit),
  });
}

export function useProcessDeletions() {
  const queryClient = useQueryClient();
  return useMutation<ProcessDeletionsResult, Error>({
    mutationFn: () => api.admin.privacy.processDeletions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'privacy', 'requests'] });
    },
  });
}
